import React, { useCallback } from 'react';
import Layout from '@splunk/react-icons/Layout';
import Button from '@splunk/react-ui/Button';
import { _ } from '@splunk/ui-utils/i18n';
import type { TabItem } from '@splunk/dashboard-types';
import {
    Modal,
    type ModalRequestCloseHandler,
    type returnFocus,
} from '../Modal';
import { StyledModal } from './StyledModal';
import { ExportConfigScreen } from './ExportConfigScreen';
import { ExportStepBar } from './ExportStepBar';
import { SearchProgressScreen } from './SearchProgressScreen';
import { TabDownloadScreen } from './TabDownloadScreen';
import { DownloadAnywayButton } from './DownloadAnywayButton';
import { RunSearchesButton } from './RunSearchesButton';
import { DownloadButton } from './DownloadButton';

interface ExportDashboardModalContentProps {
    open: boolean;
    fileType: 'pdf' | 'png';
    fileName: string;
    layoutIds: string[];
    renderTime: number;
    expandAdvancedSection?: boolean;
    modalStep: number;
    isTabbedDashboard: boolean;
    tabs: TabItem[];
    isDownloadingTabs: boolean;
    searchProgressInterval?: number;
    onFileNameChange: React.Dispatch<React.SetStateAction<string>>;
    onFileTypeChange: React.Dispatch<React.SetStateAction<'pdf' | 'png'>>;
    onLayoutIdsChange: React.Dispatch<React.SetStateAction<string[]>>;
    onRenderTimeChange: React.Dispatch<React.SetStateAction<number>>;
    onProgress: React.Dispatch<React.SetStateAction<number>>;
    onDownloadDashboard: () => Promise<void>;
    onRequestClose: () => void;
    returnFocus: returnFocus;
}

const headerIcon = <Layout />;

export const ExportModalContent = ({
    open,
    fileType,
    fileName,
    layoutIds,
    renderTime,
    returnFocus,
    expandAdvancedSection,
    modalStep,
    isTabbedDashboard,
    tabs,
    isDownloadingTabs,
    searchProgressInterval = 1000,
    onRequestClose,
    onFileNameChange,
    onFileTypeChange,
    onLayoutIdsChange,
    onRenderTimeChange,
    onDownloadDashboard,
    onProgress,
}: ExportDashboardModalContentProps) => {
    const handleModalClose = useCallback(() => {
        onRequestClose();
    }, [onRequestClose]);

    const handleRequestClickAway = useCallback<ModalRequestCloseHandler>(
        ({ reason }) => {
            if (reason === 'escapeKey' || reason === 'clickCloseButton') {
                onRequestClose();
            }
        },
        [onRequestClose]
    );

    return (
        <StyledModal
            onRequestClose={handleRequestClickAway}
            open={open}
            returnFocus={returnFocus}
            data-test="export-dashboard-modal"
        >
            <Modal.Header title={_('Download dashboard')} icon={headerIcon} />

            <Modal.Body>
                <ExportStepBar activeStepId={modalStep} />
                <ExportConfigScreen
                    modalStep={modalStep}
                    fileName={fileName}
                    fileType={fileType}
                    layoutIds={layoutIds}
                    renderTime={renderTime}
                    expandAdvancedSection={expandAdvancedSection}
                    isTabbedDashboard={isTabbedDashboard}
                    tabs={tabs}
                    onFileNameChange={onFileNameChange}
                    onFileTypeChange={onFileTypeChange}
                    onLayoutIdsChange={onLayoutIdsChange}
                    onRenderTimeChange={onRenderTimeChange}
                />
                <SearchProgressScreen
                    modalStep={modalStep}
                    isDownloadingTabs={isDownloadingTabs}
                    searchProgressInterval={searchProgressInterval}
                    onProgress={onProgress}
                />
                <TabDownloadScreen
                    modalStep={modalStep}
                    isDownloadingTabs={isDownloadingTabs}
                    tabs={tabs}
                    layoutIds={layoutIds}
                />
            </Modal.Body>

            <Modal.Footer>
                <DownloadAnywayButton
                    modalStep={modalStep}
                    onClick={onDownloadDashboard}
                />
                <Button
                    appearance="secondary"
                    onClick={handleModalClose}
                    label={_('Cancel')}
                />
                <RunSearchesButton
                    modalStep={modalStep}
                    onProgress={onProgress}
                    layoutIds={layoutIds}
                />
                <DownloadButton
                    modalStep={modalStep}
                    isDownloadingTabs={isDownloadingTabs}
                    onClick={onDownloadDashboard}
                />
            </Modal.Footer>
        </StyledModal>
    );
};
